/**
 * Product Card Mini — interactive card used in "Styled With" blocks.
 * Handles color swatch switching, option pill selection, ATC overlay,
 * hover image preview, and AJAX add-to-cart.
 *
 * Self-initializes on module load + shopify:section:load.
 * Exported init() can be called manually for dynamically inserted content.
 */
import { CartAddEvent } from '@theme/events';

const BOUND = 'data-pcm-bound';
const IMG_SEL = '.product-card-mini__image:not(.product-card-mini__image--secondary)';
const productCache = {};

// ── Splide carousel init ────────────────────────────────────────────────

function initSplide() {
  document.querySelectorAll('.styled-with-splide:not(.is-initialized)').forEach((el) => {
    if (typeof Splide === 'undefined') return;
    const { desktopColumns, mobileColumns, desktopPeek, mobilePeek, arrows, dots } = el.dataset;

    const splide = new Splide(el, {
      type: 'slide',
      perPage: parseInt(desktopColumns) || 4,
      perMove: 1,
      gap: '16px',
      arrows: arrows === 'true',
      pagination: dots === 'true',
      drag: true,
      speed: 400,
      padding: { right: `${parseInt(desktopPeek) || 0}%` },
      breakpoints: {
        768: {
          perPage: parseInt(mobileColumns) || 2,
          gap: '12px',
          padding: { right: `${parseInt(mobilePeek) || 0}%` },
        },
      },
    }).mount();

    const arrowsEl = el.querySelector('.splide__arrows');
    if (arrowsEl) {
      const updateArrows = () => {
        arrowsEl.style.display = splide.length <= splide.options.perPage ? 'none' : '';
      };
      updateArrows();
      window.addEventListener('resize', updateArrows);
    }

    el.classList.add('is-initialized');
  });
}

// ── Helpers ──────────────────────────────────────────────────────────────

function formatMoney(cents) {
  return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function getATCLabel(available) {
  const s = window.theme?.strings;
  return available ? (s?.add_to_cart ?? 'Add to Cart') : (s?.sold_out ?? 'Sold Out');
}

/**
 * Update price container to match price.liquid HTML structure.
 * Generates the same accessible markup with compare-at / sale groups.
 */
function updateCardPrice(card, variant) {
  const wrapper = card.querySelector('.product-card-mini__price');
  if (!wrapper || !variant) return;

  const price = formatMoney(variant.price);
  const hasCompare = variant.compare_at_price && variant.compare_at_price > variant.price;
  let html = '<div ref="priceContainer">';

  if (hasCompare) {
    const comparePrice = formatMoney(variant.compare_at_price);
    html +=
      `<span role="group" class="price-group--compare">` +
        `<span class="visually-hidden">Regular price\u00a0</span>` +
        `<span class="compare-at-price">${comparePrice}</span>` +
      `</span>` +
      `<span role="group" class="price-group--sale">` +
        `<span class="visually-hidden">Sale price\u00a0</span>` +
        `<span class="price">${price}</span>` +
      `</span>`;
  } else {
    html += `<span class="price">${price}</span>`;
  }

  html += '</div>';
  wrapper.innerHTML = html;
}

function fetchProductJSON(handle) {
  if (productCache[handle]) return Promise.resolve(productCache[handle]);
  return fetch(`/products/${handle}.js`)
    .then((r) => r.json())
    .then((data) => {
      productCache[handle] = data;
      return data;
    });
}

function getOptionName(opt) {
  return typeof opt === 'string' ? opt : opt.name || opt;
}

// ── Variant cache per card ──────────────────────────────────────────────
// Stored as card._variants (expando). Invalidated by rebuildCardOptions.

function getCardVariants(card) {
  if (card._variants) return card._variants;
  const el = card.querySelector('.product-card-mini__variants-json');
  if (!el) return null;
  try {
    card._variants = JSON.parse(el.textContent);
  } catch {
    return null;
  }
  return card._variants;
}

// ── Rebuild option pills + variant JSON ─────────────────────────────────

function rebuildCardOptions(card, product) {
  const form = card.querySelector('.product-card-mini__form');
  if (!form) return;

  const optionNames = (product.options || []).map(getOptionName);
  const isDefault = product.variants.length <= 1 && optionNames.length === 1 && optionNames[0] === 'Title';
  const idInput = form.querySelector('input[name="id"]');
  const atcBtn = form.querySelector('.product-card-mini__atc');

  // Remove existing non-color fieldsets + old JSON (shared by both branches)
  form.querySelectorAll('.product-card-mini__option[data-option-position]').forEach((el) => el.remove());
  form.querySelector('.product-card-mini__variants-json')?.remove();
  card._variants = null;

  const first = product.variants.find((v) => v.available) || product.variants[0];

  if (isDefault) {
    if (idInput) idInput.value = first.id;
    if (atcBtn) {
      atcBtn.disabled = !first.available;
      atcBtn.textContent = getATCLabel(first.available);
    }
    return;
  }

  // Build variant data + cache
  const variantData = product.variants.map(
    ({ id, available, price, compare_at_price, option1, option2, option3 }) => ({
      id,
      available,
      price,
      compare_at_price,
      option1,
      option2,
      option3,
    }),
  );
  card._variants = variantData;

  // Build DOM in a fragment — single insertion, single reflow
  const frag = document.createDocumentFragment();

  for (const [idx, name] of optionNames.entries()) {
    if (name.toLowerCase() === 'color') continue;
    const position = idx + 1;
    const optionKey = `option${position}`;

    const fieldset = document.createElement('fieldset');
    fieldset.className = 'product-card-mini__option';
    fieldset.dataset.optionPosition = position;

    const legend = document.createElement('legend');
    legend.className = 'product-card-mini__option-label';
    legend.textContent = name;
    fieldset.appendChild(legend);

    const valuesDiv = document.createElement('div');
    valuesDiv.className = 'product-card-mini__option-values';
    const selectedValue = first[optionKey];
    const seen = new Set();

    for (const v of product.variants) {
      const val = v[optionKey];
      if (!val || seen.has(val)) continue;
      seen.add(val);
      const isAvail = product.variants.some((vv) => vv[optionKey] === val && vv.available);
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = `product-card-mini__pill${val === selectedValue ? ' active' : ''}${!isAvail ? ' sold-out' : ''}`;
      btn.dataset.optionPosition = position;
      btn.dataset.optionValue = val;
      btn.setAttribute('aria-label', `${name}: ${val}`);
      btn.textContent = val;
      valuesDiv.appendChild(btn);
    }

    fieldset.appendChild(valuesDiv);
    frag.appendChild(fieldset);
  }

  const jsonScript = document.createElement('script');
  jsonScript.type = 'application/json';
  jsonScript.className = 'product-card-mini__variants-json';
  jsonScript.textContent = JSON.stringify(variantData);
  frag.appendChild(jsonScript);

  form.insertBefore(frag, atcBtn);
  if (idInput) idInput.value = first.id;
  if (atcBtn) {
    atcBtn.disabled = !first.available;
    atcBtn.textContent = getATCLabel(first.available);
  }
}

// ── Swatch active sync (both below-card + panel) ────────────────────────

function syncSwatchActive(card, handle) {
  card.querySelectorAll('.product-card-mini__swatch-item').forEach((li) => {
    const btn = li.querySelector('[data-swatch-target]');
    li.classList.toggle('active', !!(btn && btn.dataset.siblingHandle === handle));
  });
}

// ── ATC panel open / close ──────────────────────────────────────────────

function openATCPanel(media) {
  if (!media) return;
  media.querySelector('.product-card-mini__atc-panel')?.classList.add('is-open');
  media.querySelector('.product-card-mini__atc-trigger')?.classList.add('is-hidden');
}

function closeATCPanel(panel) {
  if (!panel) return;
  panel.classList.remove('is-open');
  panel
    .closest('.product-card-mini__media')
    ?.querySelector('.product-card-mini__atc-trigger')
    ?.classList.remove('is-hidden');
}

// ── Swatch click ────────────────────────────────────────────────────────

function handleSwatchClick(swatchBtn) {
  const card = swatchBtn.closest('.product-card-mini');
  if (!card) return;

  const {
    siblingHandle,
    siblingFeaturedImage: newSrc,
    siblingUrl: newUrl,
    siblingColorValue,
    siblingBadgeText,
    siblingBadgeHandle,
    siblingBadgeColor,
    siblingBadgeBg,
    siblingNoteCart,
    siblingNoteDisplay,
  } = swatchBtn.dataset;

  syncSwatchActive(card, siblingHandle);

  // Image swap
  const img = card.querySelector(IMG_SEL);
  if (img && newSrc) {
    img.src = newSrc;
    img.srcset = '';
  }

  // URL swap
  if (newUrl) {
    const imageLink = card.querySelector('.product-card-mini__image-link');
    const titleLink = card.querySelector('.product-card-mini__title');
    if (imageLink) imageLink.href = newUrl;
    if (titleLink) titleLink.href = newUrl;
  }

  // Hide secondary image until fetch restores it
  const secondaryImg = card.querySelector('.product-card-mini__image--secondary');
  if (secondaryImg) secondaryImg.style.display = 'none';

  // Color label
  const colorLabel = card.querySelector('.product-card-mini__color-value');
  if (colorLabel && siblingColorValue) colorLabel.textContent = siblingColorValue;

  // Badge
  const media = card.querySelector('.product-card-mini__media');
  let badge = media?.querySelector('.product-badge');
  if (siblingBadgeText) {
    if (!badge) {
      badge = document.createElement('div');
      media.appendChild(badge);
    }
    badge.className = siblingBadgeHandle
      ? `product-badge product-badge--${siblingBadgeHandle}`
      : 'product-badge';
    badge.textContent = siblingBadgeText;
    badge.style.color = siblingBadgeColor || '#000000';
    badge.style.backgroundColor = siblingBadgeBg || '#ffffff';
    badge.style.display = '';
  } else if (badge) {
    badge.style.display = 'none';
  }

  // Persistent note — hidden input for cart line properties
  let noteInput = card.querySelector('input[name="properties[Note]"]');
  if (siblingNoteCart) {
    if (!noteInput) {
      noteInput = document.createElement('input');
      noteInput.type = 'hidden';
      noteInput.name = 'properties[Note]';
      card.querySelector('.product-card-mini__form')?.appendChild(noteInput);
    }
    noteInput.value = siblingNoteCart;
  } else {
    noteInput?.remove();
  }

  // Persistent note — visual display
  const noteContainer = card.querySelector('.cart-persistent-note');
  if (noteContainer) {
    noteContainer.innerHTML = siblingNoteDisplay
      ? `<div class="cart-persistent-note__item">${siblingNoteDisplay}</div>`
      : '';
  }

  // Store originals for hover restore
  if (newSrc) card.dataset.originalImage = newSrc;
  if (newUrl) card.dataset.originalUrl = newUrl;

  // Fetch sibling for options, price, secondary image
  if (!siblingHandle) return;
  fetchProductJSON(siblingHandle)
    .then((product) => {
      rebuildCardOptions(card, product);

      const titleEl = card.querySelector('.product-card-mini__title');
      if (titleEl) titleEl.textContent = product.title;

      const first = product.variants.find((v) => v.available) || product.variants[0];
      if (first) updateCardPrice(card, first);

      if (secondaryImg && product.media?.length > 1) {
        const src = product.media[1].src || product.media[1].preview_image?.src;
        if (src) {
          secondaryImg.src = src;
          secondaryImg.srcset = '';
          secondaryImg.style.display = '';
        }
      }
    })
    .catch((err) => {
      console.warn('ProductCardMini: failed to fetch sibling', siblingHandle, err);
    });
}

// ── Pill click ──────────────────────────────────────────────────────────

function handlePillClick(pill) {
  const card = pill.closest('.product-card-mini');
  if (!card) return;

  pill
    .closest('.product-card-mini__option')
    ?.querySelectorAll('.product-card-mini__pill')
    .forEach((p) => p.classList.remove('active'));
  pill.classList.add('active');

  const variants = getCardVariants(card);
  if (!variants) return;

  const selected = {};
  card.querySelectorAll('.product-card-mini__pill.active').forEach((p) => {
    selected[`option${p.dataset.optionPosition}`] = p.dataset.optionValue;
  });

  const match = variants.find((v) => Object.keys(selected).every((k) => v[k] === selected[k]));
  if (!match) return;

  const idInput = card.querySelector('input[name="id"]');
  const atcBtn = card.querySelector('.product-card-mini__atc');
  if (idInput) idInput.value = match.id;
  if (atcBtn) {
    atcBtn.disabled = !match.available;
    atcBtn.textContent = getATCLabel(match.available);
  }
  updateCardPrice(card, match);
}

// ── Delegated click handler ─────────────────────────────────────────────

function handleBlockClick(e) {
  const t = e.target;

  const trigger = t.closest('.product-card-mini__atc-trigger');
  if (trigger) return openATCPanel(trigger.closest('.product-card-mini__media'));

  const close = t.closest('.product-card-mini__atc-close');
  if (close) return closeATCPanel(close.closest('.product-card-mini__atc-panel'));

  const swatch = t.closest('[data-swatch-target]');
  if (swatch) return handleSwatchClick(swatch);

  const pill = t.closest('.product-card-mini__pill');
  if (pill) return handlePillClick(pill);
}

// ── AJAX form submit ────────────────────────────────────────────────────

function handleFormSubmit(e) {
  const form = e.target;
  if (!form.classList?.contains('product-card-mini__form')) return;
  e.preventDefault();

  const fd = new FormData(form);
  const item = { id: parseInt(fd.get('id')), quantity: 1 };
  const props = {};
  let hasProps = false;

  fd.forEach((v, k) => {
    const m = k.match(/^properties\[(.+)\]$/);
    if (m) {
      props[m[1]] = v;
      hasProps = true;
    }
  });
  if (hasProps) item.properties = props;

  const atcBtn = form.querySelector('.product-card-mini__atc');
  if (atcBtn) {
    atcBtn.disabled = true;
    atcBtn.textContent = 'Adding...';
  }

  fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items: [item] }),
  })
    .then((r) => {
      if (!r.ok) throw new Error(r.status);
      return r.json();
    })
    .then(() => {
      // Dispatch CartAddEvent — opens cart drawer + triggers section re-render
      document.dispatchEvent(new CartAddEvent(null, null, {}));

      if (atcBtn) {
        atcBtn.textContent = 'Added!';
        setTimeout(() => {
          atcBtn.disabled = false;
          atcBtn.textContent = getATCLabel(true);
        }, 1500);
      }

      closeATCPanel(form.closest('.product-card-mini__atc-panel'));
    })
    .catch((err) => {
      console.error('ProductCardMini: cart add error', err);
      if (atcBtn) {
        atcBtn.disabled = false;
        atcBtn.textContent = getATCLabel(true);
      }
    });
}

// ── Hover preview for below-card swatches (capture phase) ───────────────

function handleSwatchHover(e, useOriginal) {
  const btn = e.target.closest('.product-card-mini__swatches [data-swatch-target]');
  if (!btn) return;
  const card = btn.closest('.product-card-mini');
  if (!card) return;
  const img = card.querySelector(IMG_SEL);
  if (!img) return;
  const src = useOriginal ? card.dataset.originalImage : btn.dataset.siblingFeaturedImage;
  if (src) {
    img.src = src;
    img.srcset = '';
  }
}

// ── Bind + Init ─────────────────────────────────────────────────────────

function bindBlock(block) {
  if (block.hasAttribute(BOUND)) return;
  block.setAttribute(BOUND, '');
  block.addEventListener('click', handleBlockClick);
  block.addEventListener('submit', handleFormSubmit);
  block.addEventListener('mouseenter', (e) => handleSwatchHover(e, false), true);
  block.addEventListener('mouseleave', (e) => handleSwatchHover(e, true), true);
}

/** Initialize all product-card-mini blocks on the page. Safe to call repeatedly. */
export function init() {
  initSplide();
  document.querySelectorAll('.product-block--styled-with').forEach(bindBlock);
}

// Self-initialize — modules are deferred so DOM is ready
init();

// Theme editor support
if (window.Shopify?.designMode) {
  document.addEventListener('shopify:section:load', init);
}
